<template>
  <div class="role-permissions-page">
    <div class="page-heading">
      <h1 class="mb-1">Role Permissions</h1>
      <p class="text-muted mb-0">Control which CRM features and actions are available to each role.</p>
    </div>

    <UserManagementTabs />

    <b-card class="assignment-card mb-4">
      <div class="d-flex align-items-center mb-3">
        <span class="assignment-icon"><i class="fas fa-key"></i></span>
        <div><h5 class="mb-0">Assign permission</h5><small class="text-muted">Add one permission to a selected role.</small></div>
      </div>
      <b-row class="align-items-end">
        <b-col cols="12" md="4">
          <b-form-group label="Role" class="mb-md-0">
            <multiselect v-model="role" label="label" track-by="value" placeholder="Select role" :options="roles" :allow-empty="true" :multiple="false" :searchable="true" :internal-search="true" :clear-on-select="false" :close-on-select="true"><span slot="noResult">No roles found</span></multiselect>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Permission" class="mb-md-0">
            <multiselect v-model="permission" label="label" track-by="value" placeholder="Select permission" :options="permissions" :allow-empty="true" :multiple="false" :searchable="true" :internal-search="true" :clear-on-select="false" :close-on-select="true"><span slot="noResult">No permissions found</span></multiselect>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2"><b-button block variant="primary" :disabled="saving || !role || !permission" @click="handleSubmit"><b-spinner v-if="saving" small></b-spinner><span v-else><i class="fas fa-plus mr-1"></i> Assign</span></b-button></b-col>
      </b-row>
    </b-card>

    <b-card class="permissions-card">
      <div class="permissions-toolbar d-flex justify-content-between align-items-center">
        <div><h5 class="mb-1">Assigned permissions</h5><small class="text-muted">{{ filteredRolePermissions.length }} assignments shown</small></div>
        <div class="permission-search input-group">
          <div class="input-group-prepend"><span class="input-group-text"><i class="fas fa-search"></i></span></div>
          <input v-model.trim="search" type="search" class="form-control" placeholder="Search role or permission...">
        </div>
      </div>

    <div class="position-relative table-responsive permissions-table-wrap">
      <div v-if="loading" class="loading-overlay">
        <LoadingIndicator centered />
      </div>

      <table class="table permissions-table mb-0">
        <thead>
          <tr>
            <TableSortableHeader label="Role" field="role_id" />
            <TableSortableHeader label="Permission" field="permission_id" />
            <th scope="col">Description</th>
            <TableSortableHeader label="Assigned" field="created_at" />
            <th scope="col" class="text-right">Action</th>
          </tr>
        </thead>
        <tbody :class="{ 'table-loading': loading }">
          <tr v-for="item in filteredRolePermissions" :key="item.id">
            <td><span class="role-pill">{{ item.role.name }}</span></td>
            <td><code class="permission-key">{{ item.permission ? item.permission.name : '—' }}</code></td>
            <td class="permission-description">{{ item.permission ? item.permission.description : 'No description' }}</td>
            <td>{{ item.createdAt | formatDate }}</td>
            <td class="text-right"><b-button variant="outline-danger" size="sm" title="Remove permission" @click="handleDelete(item)"><i class="fas fa-trash-alt mr-1"></i> Remove</b-button></td>
          </tr>
          <tr v-if="!loading && filteredRolePermissions.length === 0"><td colspan="5" class="empty-state text-center text-muted">No role permissions match your search.</td></tr>
        </tbody>
      </table>
    </div>
    </b-card>
  </div>
</template>

<script>
import { ApiClient } from '@/api/ApiClient';
import permissionsMixin from '/src/mixins/permissions';
import LoadingIndicator from '@/components/shared/LoadingIndicator.vue';
import TableSortableHeader from '@/components/shared/TableSortableHeader';
import UserManagementTabs from '@/components/pages/users/UserManagementTabs.vue';


export default {
  components: {
    LoadingIndicator,
    TableSortableHeader,
    UserManagementTabs,
  },
  mixins: [permissionsMixin],
  data() {
    return {
      rolePermissions: [],
      loading: false,
      saving: false,
      roles: [],
      permissions: [],
      role: null,
      permission: null,
      search: '',
    };
  },
  computed: {
    filteredRolePermissions() {
      const query = this.search.toLowerCase();
      if (!query) return this.rolePermissions;
      return this.rolePermissions.filter(item =>
        [item.role?.name, item.permission?.name, item.permission?.description]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(query))
      );
    },
    sortOrder() {
      const { sort } = this.$route.query;
      return sort;
    },
  },
  methods: {
    async fetchRoles() {
      const response = await ApiClient.roles.get({});
      if (response.status == 200) {
        this.roles = response.data.data.map(u => ({ value: u.id, label: u.name }));
      }
    },
    async fetchPermissions() {
      const response = await ApiClient.permissions.get({});
      if (response.status == 200) {
        this.permissions = response.data.data.map(u => ({ value: u.id, label: u.name + " | " + u.description }));
      }
    },
    async fetch() {
      if (this.loading) return;
      this.loading = true;

      let opts = {
        sort: this.sortOrder,
      };
      
      const response = await ApiClient.rolePermissions.get({params: opts});
      this.loading = false;
      if (response.status == 200) {
        this.rolePermissions = response.data.data;
      }
    },
    async handleDelete(item) {
      let text = "Remove " + (item.permission? item.permission.name : ' - ') + " from " + item.role.name + "?";
      if (confirm(text) == true) {
        await ApiClient.rolePermissions.delete({id: item.id});
        this.fetch();
      } 
    },
    async handleSubmit() {
      this.saving = true;
      await ApiClient.rolePermissions.post({body : {role_id: this.role.value, permission_id: this.permission.value}});
      this.saving = false;
      this.role = null;
      this.permission = null;
      this.fetch();
    }
  },

  watch: {
    $route: {
      immediate: true,
      handler: function () {
        this.fetch();
      },
    },
  },

  async mounted() {
    this.fetchRoles();
    this.fetchPermissions();
  },

};
</script>

<style lang="scss" scoped>
.page-heading { margin-bottom: 1.25rem; }
.assignment-card { margin-top: 0; border-top-left-radius: 0; }
.assignment-card,.permissions-card { border: 1px solid #dfe3e8; box-shadow: none; }
.assignment-icon { display: inline-flex; width: 38px; height: 38px; margin-right: 0.75rem; align-items: center; justify-content: center; border-radius: 50%; color: #1676df; background: #e7f1fb; }
.permissions-toolbar { gap: 1rem; padding-bottom: 1rem; }
.permission-search { width: 320px; max-width: 100%; }
.permissions-table-wrap { border: 1px solid #dfe3e8; border-radius: 0.35rem; }
.permissions-table thead th { padding: 0.8rem 0.9rem; border-top: 0; border-bottom: 1px solid #dfe3e8; background: #f6f7f9; color: #5f6977; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.035em; text-transform: uppercase; white-space: nowrap; }
.permissions-table tbody td { padding: 0.72rem 0.9rem; vertical-align: middle; border-top-color: #e7eaee; color: #343a40; }
.permissions-table tbody tr:hover { background: #f7f9fb; }
.role-pill { display: inline-block; padding: 0.25rem 0.55rem; border-radius: 999px; color: #3c628a; background: #e7f1fb; font-size: 0.76rem; font-weight: 700; }
.permission-key { padding: 0.22rem 0.45rem; color: #76520b; background: #fff5d6; border-radius: 0.25rem; font-size: 0.78rem; }
.permission-description { color: #5f6977 !important; }
.empty-state { padding: 2.5rem !important; }
.table-loading {
  opacity: 0.5;
}
.loading-overlay {
  position: absolute;
  left: 50%;
  top: 50%;
}
.dark-style {
  .assignment-card,.permissions-card,.permissions-table-wrap { border-color: rgba(255,255,255,0.08); }
  .assignment-icon { color: #8fc7ff; background: rgba(44,123,204,0.18); }
  .permissions-table thead th { border-bottom-color: rgba(255,255,255,0.12); background: transparent; color: #aeb4bd; }
  .permissions-table tbody td { border-top-color: rgba(255,255,255,0.06); color: inherit; }
  .permissions-table tbody tr:hover { background: rgba(255,255,255,0.035); }
  .role-pill { color: #8fc7ff; background: rgba(44,123,204,0.18); }
  .permission-key { color: #ffd166; background: rgba(255,193,7,0.12); }
  .permission-description { color: #c3c8d0 !important; }
}
@media(max-width:767px){.permissions-toolbar{align-items:stretch!important;flex-direction:column}.permission-search{width:100%}}
</style>
